"use client";
import { useCallback, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { NodeSelection } from "@tiptap/pm/state"
import { CellSelection } from "@tiptap/pm/tables"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"

// --- Icons ---
import { TrashIcon } from "@/tiptap/icons/trash-icon"

export const DELETE_NODE_SHORTCUT_KEY = "backspace"

// Structural children that should not be deleted on their own — delete the
// enclosing parent instead (same idea as skipping table cells/rows).
const SKIP_DELETE_NODE_TYPES = new Set([
  "tableRow",
  "tableHeader",
  "tableCell",
  "detailsSummary",
  "detailsContent",
])

// When the caret/selection is inside these containers (accordion title/body,
// table cell, etc.), Delete should remove the whole container — matching
// drag-handle NodeSelection behavior on desktop.
const PREFERRED_DELETE_ANCESTOR_TYPES = new Set(["details", "table"])

/**
 * Checks if a node can be deleted based on the current selection
 */
export function canDeleteNode(editor) {
  if (!editor || !editor.isEditable) return false

  const { state } = editor
  const { selection } = state

  if (selection instanceof NodeSelection) {
    return true
  }

  const $pos = selection.$anchor

  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth)
    const pos = $pos.before(depth)

    // Check if we could delete the range from pos to pos + nodeSize
    const tr = state.tr.delete(pos, pos + node.nodeSize)
    if (tr.doc !== state.doc) {
      return true
    }
  }

  return false
}

/**
 * Helper function to delete a node with fallback strategy
 */
export function deleteNodeAtPosition(editor, pos, nodeSize) {
  const chain = editor.chain().focus()
  const success = chain.deleteRange({ from: pos, to: pos + nodeSize }).run()

  if (success) return true

  // Fallback
  return chain.setNodeSelection(pos).deleteSelection().run();
}

function findPreferredDeleteAncestor($pos) {
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth)
    if (node && PREFERRED_DELETE_ANCESTOR_TYPES.has(node.type.name)) {
      return { pos: $pos.before(depth), node }
    }
  }

  return null
}

/**
 * Deletes the selected node in the editor using current selection
 */
export function deleteNode(editor) {
  if (!editor || !editor.isEditable) return false

  try {
    const { state } = editor
    const { selection } = state

    if (selection instanceof NodeSelection) {
      const pos = selection.from
      const selectedNode = selection.node

      if (!selectedNode) return false

      return deleteNodeAtPosition(editor, pos, selectedNode.nodeSize);
    }

    // CellSelection (table handles / multi-cell select) and caret-inside
    // accordion/table: delete the enclosing block, not an inner paragraph.
    const $pos =
      selection instanceof CellSelection ? selection.$anchorCell : selection.$from

    const preferred = findPreferredDeleteAncestor($pos)
    if (preferred) {
      return deleteNodeAtPosition(editor, preferred.pos, preferred.node.nodeSize)
    }

    for (let depth = $pos.depth; depth > 0; depth--) {
      const node = $pos.node(depth)
      const pos = $pos.before(depth)

      if (
        node &&
        node.isBlock &&
        !SKIP_DELETE_NODE_TYPES.has(node.type.name)
      ) {
        return deleteNodeAtPosition(editor, pos, node.nodeSize);
      }
    }

    return false
  } catch {
    return false
  }
}

/**
 * Determines if the delete node button should be shown
 */
export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!editor.isActive("code")) {
    return canDeleteNode(editor);
  }

  return true
}

/**
 * Custom hook that provides delete node functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage - no params needed
 * function MySimpleDeleteButton() {
 *   const { isVisible, handleDeleteNode } = useDeleteNode()
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleDeleteNode}>Delete</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedDeleteButton() {
 *   const { isVisible, handleDeleteNode, label } = useDeleteNode({
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onDeleted: () => console.log('Node deleted!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleDeleteNode}
 *       aria-label={label}
 *     >
 *       Delete Node
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useDeleteNode(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onDeleted,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()
  const [isVisible, setIsVisible] = useState(true)
  const canDeleteNodeState = canDeleteNode(editor)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  const handleDeleteNode = useCallback(() => {
    if (!editor) return false

    const success = deleteNode(editor)
    if (success) {
      onDeleted?.()
    }
    return success
  }, [editor, onDeleted])

  useHotkeys(DELETE_NODE_SHORTCUT_KEY, (event) => {
    event.preventDefault()
    handleDeleteNode()
  }, {
    enabled: isVisible && canDeleteNodeState,
    enableOnContentEditable: !isMobile,
    enableOnFormTags: true,
  })

  return {
    isVisible,
    handleDeleteNode,
    canDeleteNode: canDeleteNodeState,
    label: "Delete",
    shortcutKeys: DELETE_NODE_SHORTCUT_KEY,
    Icon: TrashIcon,
  }
}
